// src/pages/user-profile-settings/components/BusinessProfile.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BusinessProfile = ({ user, onDataChange }) => {
  const [businessData, setBusinessData] = useState({
    licenseNumber: 'AG-123456789',
    licenseState: 'California', 
    licenseExpiry: '2025-06-15',
    farmName: 'Green Valley Agricultural Co.',
    farmAddress: '456 Rural Route 1, Farmington Valley',
    farmPhone: '+1 (555) 987-6543',
    farmWebsite: 'https://www.greenvalleyag.com',
    yearsExperience: '10',
    languages: ['English', 'Spanish'],
    certifications: ['Organic Certified', 'GAP Certified', 'USDA Certified'],
    specializations: ['Organic Vegetables', 'Seasonal Fruits', 'Livestock Feed', 'Dairy Products'],
    serviceAreas: ['Northern California', 'Central Valley', 'Bay Area'],
    commission: '2.5',
    availability: 'full-time',
    farmSize: '250',
    farmType: 'mixed-crops'
  });

  const [showMapModal, setShowMapModal] = useState(false);
  const [newSpecialization, setNewSpecialization] = useState('');
  const [newServiceArea, setNewServiceArea] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newCertification, setNewCertification] = useState('');

  const handleInputChange = (field, value) => {
    setBusinessData(prev => ({ ...prev, [field]: value }));
    onDataChange?.();
  };

  const handleArrayAdd = (field, value, setter) => {
    if (value?.trim() && !businessData?.[field]?.includes(value?.trim())) {
      setBusinessData(prev => ({
        ...prev,
        [field]: [...prev?.[field], value?.trim()]
      }));
      setter('');
      onDataChange?.();
    }
  };

  const handleArrayRemove = (field, index) => {
    setBusinessData(prev => ({
      ...prev,
      [field]: prev?.[field]?.filter((_, i) => i !== index)
    }));
    onDataChange?.();
  };

  const availabilityOptions = [
    { value: 'full-time', label: 'Full-time Operations' },
    { value: 'seasonal', label: 'Seasonal' },
    { value: 'weekends', label: 'Weekends Only' },
    { value: 'harvest-season', label: 'Harvest Season Only' }
  ];

  const farmTypeOptions = [
    { value: 'mixed-crops', label: 'Mixed Crops' },
    { value: 'vegetables', label: 'Vegetables' },
    { value: 'fruits', label: 'Fruits' },
    { value: 'grains', label: 'Grains' },
    { value: 'livestock', label: 'Livestock' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'organic', label: 'Organic Farming' }
  ];

  const predefinedSpecializations = [
    'Organic Vegetables', 'Seasonal Fruits', 'Livestock Feed', 'Dairy Products',
    'Grain Production', 'Sustainable Farming', 'Hydroponic Systems',
    'Free-Range Poultry', 'Organic Certification', 'Farm-to-Table'
  ];

  return (
    <div className="space-y-6">
      {/* Farm License/Certification Information */}
      <div className="bg-surface rounded-lg shadow-elevation-1">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary font-heading">
            Farm Certification & Licensing
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Manage your agricultural certifications and permits
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="licenseNumber" className="block text-sm font-medium text-text-primary mb-2">
                Farm License/Permit Number *
              </label>
              <input
                type="text"
                id="licenseNumber"
                value={businessData?.licenseNumber}
                onChange={(e) => handleInputChange('licenseNumber', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                required
              />
            </div>
            
            <div>
              <label htmlFor="licenseState" className="block text-sm font-medium text-text-primary mb-2">
                Operating State/Region *
              </label>
              <select
                id="licenseState"
                value={businessData?.licenseState}
                onChange={(e) => handleInputChange('licenseState', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                required
              >
                <option value="California">California</option>
                <option value="Texas">Texas</option>
                <option value="Florida">Florida</option>
                <option value="Iowa">Iowa</option>
                <option value="Nebraska">Nebraska</option>
                {/* Add more states as needed */}
              </select>
            </div>
            
            <div>
              <label htmlFor="licenseExpiry" className="block text-sm font-medium text-text-primary mb-2">
                Certification Expiry *
              </label>
              <input
                type="date"
                id="licenseExpiry"
                value={businessData?.licenseExpiry}
                onChange={(e) => handleInputChange('licenseExpiry', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                required
              />
            </div>
          </div>
        </div>
      </div>
      {/* Farm Details */}
      <div className="bg-surface rounded-lg shadow-elevation-1">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary font-heading">
            Farm Information
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Details about your agricultural operation
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="farmName" className="block text-sm font-medium text-text-primary mb-2">
                Farm/Business Name *
              </label>
              <input
                type="text"
                id="farmName"
                value={businessData?.farmName}
                onChange={(e) => handleInputChange('farmName', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                required
              />
            </div>
            
            <div>
              <label htmlFor="farmPhone" className="block text-sm font-medium text-text-primary mb-2">
                Farm Phone
              </label>
              <input
                type="tel"
                id="farmPhone"
                value={businessData?.farmPhone}
                onChange={(e) => handleInputChange('farmPhone', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              />
            </div>
            
            <div className="md:col-span-2">
              <label htmlFor="farmAddress" className="block text-sm font-medium text-text-primary mb-2">
                Farm Address
              </label>
              <input
                type="text"
                id="farmAddress"
                value={businessData?.farmAddress}
                onChange={(e) => handleInputChange('farmAddress', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              />
            </div>
            
            <div>
              <label htmlFor="farmWebsite" className="block text-sm font-medium text-text-primary mb-2">
                Farm Website
              </label>
              <input
                type="url"
                id="farmWebsite"
                value={businessData?.farmWebsite}
                onChange={(e) => handleInputChange('farmWebsite', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                placeholder="https://"
              />
            </div>

            <div>
              <label htmlFor="farmSize" className="block text-sm font-medium text-text-primary mb-2">
                Farm Size (acres)
              </label>
              <input
                type="number"
                id="farmSize"
                value={businessData?.farmSize}
                onChange={(e) => handleInputChange('farmSize', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                min="1"
              />
            </div>

            <div>
              <label htmlFor="farmType" className="block text-sm font-medium text-text-primary mb-2">
                Farm Type
              </label>
              <select
                id="farmType"
                value={businessData?.farmType}
                onChange={(e) => handleInputChange('farmType', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              >
                {farmTypeOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      {/* Distribution Areas */}
      <div className="bg-surface rounded-lg shadow-elevation-1">
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-text-primary font-heading">
                Distribution Areas
              </h2>
              <p className="text-sm text-text-secondary mt-1">
                Define the regions where you distribute your products
              </p>
            </div>
            <button
              onClick={() => setShowMapModal(true)}
              className="bg-primary text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <Icon name="Map" size={16} />
              <span>Map Selection</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {businessData?.serviceAreas?.map((area, index) => (
              <span
                key={index}
                className="inline-flex items-center space-x-2 bg-primary-100 text-primary px-3 py-1 rounded-full text-sm"
              >
                <span>{area}</span>
                <button
                  onClick={() => handleArrayRemove('serviceAreas', index)}
                  className="text-primary hover:text-primary-700"
                >
                  <Icon name="X" size={14} />
                </button>
              </span>
            ))}
          </div>
          
          <div className="flex space-x-2">
            <input
              type="text"
              value={newServiceArea}
              onChange={(e) => setNewServiceArea(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleArrayAdd('serviceAreas', newServiceArea, setNewServiceArea)}
              placeholder="Add service area"
              className="flex-1 px-4 py-2 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
            />
            <button
              onClick={() => handleArrayAdd('serviceAreas', newServiceArea, setNewServiceArea)}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors duration-200"
            >
              Add
            </button>
          </div>
        </div>
      </div>
      {/* Agricultural Specializations */}
      <div className="bg-surface rounded-lg shadow-elevation-1">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary font-heading">
            Agricultural Specializations
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Highlight your farming expertise and product specialities
          </p>
        </div>

        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {businessData?.specializations?.map((spec, index) => (
              <span
                key={index}
                className="inline-flex items-center space-x-2 bg-accent-100 text-accent-600 px-3 py-1 rounded-full text-sm"
              >
                <span>{spec}</span>
                <button
                  onClick={() => handleArrayRemove('specializations', index)}
                  className="text-accent-600 hover:text-accent-700"
                >
                  <Icon name="X" size={14} />
                </button>
              </span>
            ))}
          </div>
          
          <div className="space-y-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newSpecialization}
                onChange={(e) => setNewSpecialization(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleArrayAdd('specializations', newSpecialization, setNewSpecialization)}
                placeholder="Add custom specialization"
                className="flex-1 px-4 py-2 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              />
              <button
                onClick={() => handleArrayAdd('specializations', newSpecialization, setNewSpecialization)}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                Add
              </button>
            </div>
            
            <div>
              <p className="text-sm font-medium text-text-primary mb-2">Quick Add:</p>
              <div className="flex flex-wrap gap-2">
                {predefinedSpecializations?.filter(spec => !businessData?.specializations?.includes(spec))?.map((spec) => (
                    <button
                      key={spec}
                      onClick={() => handleArrayAdd('specializations', spec, () => {})}
                      className="text-xs bg-secondary-100 text-text-secondary px-2 py-1 rounded hover:bg-secondary-200 transition-colors duration-200"
                    >
                      + {spec}
                    </button>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Professional Agricultural Details */}
      <div className="bg-surface rounded-lg shadow-elevation-1">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary font-heading">
            Agricultural Business Details
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Additional information about your farming operation
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="yearsExperience" className="block text-sm font-medium text-text-primary mb-2">
                Years of Farming Experience
              </label>
              <input
                type="number"
                id="yearsExperience"
                value={businessData?.yearsExperience}
                onChange={(e) => handleInputChange('yearsExperience', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                min="0"
                max="50"
              />
            </div>
            
            <div>
              <label htmlFor="availability" className="block text-sm font-medium text-text-primary mb-2">
                Operation Schedule
              </label>
              <select
                id="availability"
                value={businessData?.availability}
                onChange={(e) => handleInputChange('availability', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              >
                {availabilityOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="commission" className="block text-sm font-medium text-text-primary mb-2">
                Commission Rate (%)
              </label>
              <input
                type="number"
                id="commission"
                value={businessData?.commission}
                onChange={(e) => handleInputChange('commission', e?.target?.value)}
                className="block w-full px-4 py-3 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                min="0"
                max="10"
                step="0.1"
              />
            </div>
          </div>
          
          {/* Languages */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Languages Spoken
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              {businessData?.languages?.map((lang, index) => (
                <span
                  key={index}
                  className="inline-flex items-center space-x-2 bg-success-100 text-success px-3 py-1 rounded-full text-sm"
                >
                  <span>{lang}</span>
                  <button
                    onClick={() => handleArrayRemove('languages', index)}
                    className="text-success hover:text-success-600"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </span>
              ))}
            </div>
            
            <div className="flex space-x-2">
              <input
                type="text"
                value={newLanguage}
                onChange={(e) => setNewLanguage(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleArrayAdd('languages', newLanguage, setNewLanguage)}
                placeholder="Add language"
                className="flex-1 px-4 py-2 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              />
              <button
                onClick={() => handleArrayAdd('languages', newLanguage, setNewLanguage)}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                Add
              </button>
            </div>
          </div>
          
          {/* Agricultural Certifications */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Agricultural Certifications
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              {businessData?.certifications?.map((cert, index) => (
                <span
                  key={index}
                  className="inline-flex items-center space-x-2 bg-warning-100 text-warning px-3 py-1 rounded-full text-sm"
                >
                  <span>{cert}</span>
                  <button
                    onClick={() => handleArrayRemove('certifications', index)}
                    className="text-warning hover:text-warning-600"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </span>
              ))}
            </div>
            
            <div className="flex space-x-2">
              <input
                type="text"
                value={newCertification}
                onChange={(e) => setNewCertification(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleArrayAdd('certifications', newCertification, setNewCertification)}
                placeholder="Add certification (e.g., Organic Certified, GAP Certified, USDA Certified)"
                className="flex-1 px-4 py-2 border border-border rounded-md focus:border-border-focus focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              />
              <button
                onClick={() => handleArrayAdd('certifications', newCertification, setNewCertification)}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Map Modal */}
      {showMapModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-center justify-center p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary">Select Service Areas</h3>
              <button
                onClick={() => setShowMapModal(false)}
                className="text-text-secondary hover:text-text-primary"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="h-96 bg-secondary-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Map" size={64} className="text-secondary-300 mx-auto mb-4" />
                  <p className="text-text-secondary">
                    Interactive map for service area selection would be implemented here
                  </p>
                  <p className="text-sm text-text-secondary mt-2">
                    Integration with mapping services like Google Maps or Mapbox
                  </p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowMapModal(false)}
                className="border border-border text-text-secondary px-4 py-2 rounded-md font-medium hover:bg-secondary-100 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowMapModal(false)}
                className="bg-primary text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 transition-colors duration-200"
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessProfile;